import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook for creating, updating, and deleting collections.
 */
export function useCollectionMutations() {
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  const createCollection = (props: {
    identifier: string;
    title: string;
    description?: string;
  }) => {
    return new Promise<void>((resolve, reject) => {
      publishEvent(
        {
          kind: 30005,
          content: '',
          tags: [
            ['d', props.identifier],
            ['title', props.title],
            ...(props.description ? [['description', props.description]] : []),
          ],
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collections', user?.pubkey] });
            resolve();
          },
          onError: reject,
        }
      );
    });
  };

  const updateCollection = (props: {
    existingEvent: NostrEvent;
    title?: string;
    description?: string;
    videoIds?: string[];
  }) => {
    return new Promise<void>((resolve, reject) => {
      const existingTags = props.existingEvent.tags;

      // Build new tags
      const tags: string[][] = [['d', existingTags.find(t => t[0] === 'd')?.[1] ?? '']];

      // Add title
      if (props.title !== undefined) {
        tags.push(['title', props.title]);
      } else {
        // Preserve existing title
        const existingTitle = existingTags.find(t => t[0] === 'title');
        if (existingTitle) tags.push(existingTitle);
      }

      // Add description (optional)
      if (props.description !== undefined) {
        tags.push(['description', props.description]);
      } else {
        const existingDesc = existingTags.find(t => t[0] === 'description');
        if (existingDesc) tags.push(existingDesc);
      }

      // Add video references if provided
      if (props.videoIds) {
        for (const videoId of props.videoIds) {
          tags.push(['e', videoId]);
        }
      } else {
        // Preserve existing video references
        for (const tag of existingTags) {
          if (tag[0] === 'e') tags.push(tag as string[]);
        }
      }

      // Add alt tag for accessibility
      tags.push(['alt', 'Video collection']);

      publishEvent(
        {
          kind: 30005,
          content: '',
          tags,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collections', user?.pubkey] });
            queryClient.invalidateQueries({ queryKey: ['collection', user?.pubkey, tags[0][1]] });
            resolve();
          },
          onError: reject,
        }
      );
    });
  };

  const deleteCollection = (eventId: string) => {
    return new Promise<void>((resolve, reject) => {
      // To delete a replaceable event, we publish an empty one with the same d-tag
      // First we need to get the d-tag from the existing event
      const getDTag = async () => {
        // In a real implementation, we'd fetch the event to get its d-tag
        // For now, we'll rely on the caller to pass the d-tag
      };

      // Note: Deleting replaceable events isn't standard - we just let users not update them
      // A proper delete would require the d-tag from the original event
      console.warn('Delete not fully implemented - requires d-tag from original event');
      resolve();
    });
  };

  const addToCollection = (props: {
    collection: NostrEvent;
    videoId: string;
  }) => {
    return new Promise<void>((resolve, reject) => {
      const existingTags = props.collection.tags;
      const videoIds = existingTags
        .filter(t => t[0] === 'e')
        .map(t => t[1]);

      // Avoid duplicates
      if (videoIds.includes(props.videoId)) {
        resolve();
        return;
      }

      videoIds.push(props.videoId);

      const title = existingTags.find(t => t[0] === 'title');
      const description = existingTags.find(t => t[0] === 'description');

      const tags: string[][] = [
        ['d', existingTags.find(t => t[0] === 'd')?.[1] ?? ''],
        ['title', title?.[1] ?? 'Untitled'],
        ...(description ? [description] : []),
        ['e', props.videoId],
      ];

      // Add existing video refs
      for (const id of videoIds) {
        if (id !== props.videoId) {
          tags.push(['e', id]);
        }
      }

      tags.push(['alt', 'Video collection']);

      publishEvent(
        {
          kind: 30005,
          content: '',
          tags,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collections', user?.pubkey] });
            queryClient.invalidateQueries({ queryKey: ['collection', user?.pubkey, tags[0][1]] });
            resolve();
          },
          onError: reject,
        }
      );
    });
  };

  const removeFromCollection = (props: {
    collection: NostrEvent;
    videoId: string;
  }) => {
    return new Promise<void>((resolve, reject) => {
      const existingTags = props.collection.tags;
      const videoIds = existingTags
        .filter(t => t[0] === 'e')
        .map(t => t[1])
        .filter(id => id !== props.videoId);

      const title = existingTags.find(t => t[0] === 'title');
      const description = existingTags.find(t => t[0] === 'description');

      const tags: string[][] = [
        ['d', existingTags.find(t => t[0] === 'd')?.[1] ?? ''],
        ['title', title?.[1] ?? 'Untitled'],
        ...(description ? [description] : []),
      ];

      for (const id of videoIds) {
        tags.push(['e', id]);
      }

      tags.push(['alt', 'Video collection']);

      publishEvent(
        {
          kind: 30005,
          content: '',
          tags,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collections', user?.pubkey] });
            queryClient.invalidateQueries({ queryKey: ['collection', user?.pubkey, tags[0][1]] });
            resolve();
          },
          onError: reject,
        }
      );
    });
  };

  return {
    createCollection,
    updateCollection,
    deleteCollection,
    addToCollection,
    removeFromCollection,
    isPending,
  };
}